import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatVariantLabel } from "@/lib/variants";
import { AlertTriangle, Truck } from "lucide-react";

const fmtQ = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 });
const fmtD = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : null);

/**
 * Satış sihirbazı — yolda stok görünürlüğü (FAZ: yolda stok).
 * Mevcut /inventory/stock-availability + /purchase-orders reuse; salt-okur panel.
 */
export default function WizardStockInfo({ productId, warehouseId, sizeQuantities = {}, totalQuantity = 0, variants = [], orderMode = false }) {
  const [rows, setRows] = useState([]);
  const [etaMap, setEtaMap] = useState({});

  useEffect(() => {
    if (!productId) { setRows([]); setEtaMap({}); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/inventory/stock-availability", {
          params: { product_ids: productId, ...(warehouseId ? { warehouse_id: warehouseId } : {}) },
        });
        if (alive) setRows(Array.isArray(data) ? data : []);
      } catch { if (alive) setRows([]); }
      // Tahmini geliş: bu ürün için açık PO'lardan en erken termin
      try {
        const { data: pos } = await api.get("/purchase-orders");
        const eta = {};
        for (const po of pos || []) {
          if (!["ordered", "partial_received"].includes(po.status || "")) continue;
          const ed = po.expected_delivery_date;
          if (!ed) continue;
          for (const it of po.items || []) {
            if (it.product_id !== productId) continue;
            const k = it.variant_id || "";
            if (!eta[k] || new Date(ed) < new Date(eta[k])) eta[k] = ed;
          }
        }
        if (alive) setEtaMap(eta);
      } catch { /* sessiz */ }
    })();
    return () => { alive = false; };
  }, [productId, warehouseId]);

  const byVariant = useMemo(() => {
    const m = {};
    for (const r of rows) m[r.variant_id || ""] = r;
    return m;
  }, [rows]);

  if (!productId) return null;

  const varList = variants.length > 0 ? variants : [{ id: "", attributes: {}, sku: "" }];

  return (
    <div className="rounded-sm border border-slate-800 bg-slate-950/50 overflow-hidden" data-testid="wizard-stock-info">
      <div className="px-3 py-2 border-b border-slate-800 text-[10px] font-mono uppercase tracking-widest text-slate-500">
        Stok Durumu {warehouseId ? "" : "(depo seçilmedi — tüm depolar)"}
      </div>
      {rows.length === 0 ? (
        <div className="px-3 py-2.5 text-[11px] text-slate-500">Bu ürün için stok kaydı yok.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="text-[9px] uppercase tracking-widest text-slate-600 border-b border-slate-800/60">
                <th className="text-left px-3 py-1.5">Varyant</th>
                <th className="text-right px-2 py-1.5">Fiziksel</th>
                <th className="text-right px-2 py-1.5">Rezerve</th>
                <th className="text-right px-2 py-1.5">Kul. Fiziksel</th>
                <th className="text-right px-2 py-1.5">Yolda</th>
                <th className="text-right px-2 py-1.5">Yoldan Rez.</th>
                <th className="text-right px-2 py-1.5">Kul. Yolda</th>
                <th className="text-right px-3 py-1.5">Toplam Kul.</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/40">
              {varList.map((v) => {
                const r = byVariant[v.id || ""];
                if (!r) return null;
                const eta = fmtD(etaMap[v.id || ""]);
                return (
                  <tr key={v.id || "gen"} data-testid={`wsi-row-${v.id || "gen"}`}>
                    <td className="px-3 py-1.5 text-slate-300 truncate max-w-24">
                      {variants.length > 0 ? formatVariantLabel(v.attributes, v.sku || "—") : "Genel"}
                      {eta && r.incoming > 0 && (
                        <span className="ml-1.5 text-sky-400 text-[9px]" data-testid={`wsi-eta-${v.id || "gen"}`}>
                          <Truck className="w-2.5 h-2.5 inline mr-0.5" />{eta}
                        </span>
                      )}
                    </td>
                    <td className="text-right px-2 py-1.5 text-slate-200 tabular-nums">{fmtQ(r.physical)}</td>
                    <td className="text-right px-2 py-1.5 text-slate-400 tabular-nums">{fmtQ(r.reserved_physical)}</td>
                    <td className="text-right px-2 py-1.5 text-slate-100 tabular-nums">{fmtQ(r.available_physical)}</td>
                    <td className="text-right px-2 py-1.5 text-sky-300 tabular-nums">{fmtQ(r.incoming)}</td>
                    <td className="text-right px-2 py-1.5 text-slate-400 tabular-nums">{fmtQ(r.reserved_incoming)}</td>
                    <td className="text-right px-2 py-1.5 text-sky-200 tabular-nums">{fmtQ(r.available_incoming)}</td>
                    <td className="text-right px-3 py-1.5 text-emerald-300 font-semibold tabular-nums">{fmtQ(r.available_total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Uyarılar: fiziksel yetmiyor ama yolda yetiyor / hiçbiri yetmiyor */}
      {varList.map((v) => {
        const r = byVariant[v.id || ""];
        const q = Number((sizeQuantities || {})[v.id] || (variants.length === 0 ? totalQuantity : 0) || 0);
        if (!r || q <= 0) return null;
        const label = variants.length > 0 ? formatVariantLabel(v.attributes, v.sku || "—") : "Genel";
        if (orderMode && q > r.available_physical) {
          const fromStock = Math.max(0, r.available_physical);
          return (
            <div key={v.id || "gen"} className="px-3 py-1.5 text-[11px] text-amber-300 bg-amber-500/10 border-t border-amber-500/20"
              data-testid={`wsi-supply-info-${v.id || "gen"}`}>
              {label}: {fmtQ(fromStock)} adet stoktan karşılanabilir. Kalan {fmtQ(q - fromStock)} adet için tedarik yöntemi seçiniz.
            </div>
          );
        }
        if (q > r.available_physical && q <= r.available_total) {
          const fromIncoming = q - r.available_physical;
          return (
            <div key={v.id || "gen"} className="px-3 py-1.5 text-[11px] text-amber-300 bg-amber-500/10 border-t border-amber-500/20"
              data-testid={`wsi-warning-${v.id || "gen"}`}>
              <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
              {label}: Depoda yeterli stok yok. {fmtQ(fromIncoming)} adet yoldaki stoktan rezerve edilecek.
              {etaMap[v.id || ""] ? ` Tahmini geliş: ${fmtD(etaMap[v.id || ""])}.` : ""}
            </div>
          );
        }
        if (q > r.available_total) {
          return (
            <div key={v.id || "gen"} className="px-3 py-1.5 text-[11px] text-rose-300 bg-rose-500/10 border-t border-rose-500/20"
              data-testid={`wsi-insufficient-${v.id || "gen"}`}>
              <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
              {label}: Yetersiz stok — fiziksel + yolda toplam {fmtQ(r.available_total)} adet kullanılabilir.
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}
